/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
import { defineAsyncComponent } from 'vue'

/**
 * Get async components and imports.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-snippet': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-snippet' */'~async/accordion-snippet/accordion-snippet') }),
    'animated-right-arrow': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.animated-right-arrow' */'~async/animated-right-arrow/animated-right-arrow') }),
    'animated-text': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.animated-text' */'~async/animated-text/animated-text') }),
    'blog-grid': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.blog-grid' */'~async/blog-grid/blog-grid') }),
    'blog-navigation': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.blog-navigation' */'~async/blog-navigation/blog-navigation') }),
    'boost-content-switcher': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.boost-content-switcher' */'~async/boost-content-switcher/boost-content-switcher') }),
    'boost-drawer-mobile': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.boost-drawer-mobile' */'~async/boost-drawer-mobile/boost-drawer-mobile') }),
    'breadcrumbs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.breadcrumbs' */'~async/breadcrumbs/breadcrumbs') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'colour-swatches': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.colour-swatches' */'~async/colour-swatches/colour-swatches') }),
    'column-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.column-section' */'~async/column-section/column-section') }),
    'column-tabs-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.column-tabs-section' */'~async/column-tabs-section/column-tabs-section') }),
    'complete-the-look': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.complete-the-look' */'~async/complete-the-look/complete-the-look') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers-account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers-activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers-addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers-login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers-order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers-orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers-register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers-reset-password/customers-reset-password') }),
    'empty-header-space': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.empty-header-space' */'~async/empty-header-space/empty-header-space') }),
    'free-gift': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.free-gift' */'~async/free-gift/free-gift') }),
    'full-width-story': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.full-width-story' */'~async/full-width-story/full-width-story') }),
    'gift-wrap': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.gift-wrap' */'~async/gift-wrap/gift-wrap') }),
    'klarna-messaging': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.klarna-messaging' */'~async/klarna-messaging/klarna-messaging') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-faq': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-faq' */'~async/main-faq/main-faq') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'multi-store-modal': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-store-modal' */'~async/multi-store-modal/multi-store-modal') }),
    'newsletter-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.newsletter-form' */'~async/newsletter-form/newsletter-form') }),
    'page-size-guide': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.page-size-guide' */'~async/page-size-guide/page-size-guide') }),
    'predictive-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.predictive-search' */'~async/predictive-search/predictive-search') }),
    'predictive-search-bar': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.predictive-search-bar' */'~async/predictive-search-bar/predictive-search-bar') }),
    'product-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-accordion' */'~async/product-accordion/product-accordion') }),
    'product-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-carousel' */'~async/product-carousel/product-carousel') }),
    'product-gallery': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-gallery' */'~async/product-gallery/product-gallery') }),
    'product-variants': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-variants' */'~async/product-variants/product-variants') }),
    'signup-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.signup-form' */'~async/signup-form/signup-form') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer/site-footer') }),
    'size-guide-tables': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.size-guide-tables' */'~async/size-guide-tables/size-guide-tables') }),
    'storytelling-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.storytelling-section' */'~async/storytelling-section/storytelling-section') }),
    // 'cnvs-featured-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cnvs-featured-products' */'~async/cnvs-featured-products/cnvs-featured-products') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components here.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import MultiStore from '~global/multi-store/multi-store'
import MultiStoreOptions from '~global/multi-store-options/multi-store-options'
import Nosto from '~global/nosto/nosto'
import Overlay from '~global/overlay/overlay'
import PreHeaderBanner from '~global/site-header/pre-header-banner'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import ResponsiveVideo from '~global/responsive-video/responsive-video'
import SiteHeader from '~global/site-header/site-header'
import TransitionHeight from '~global/transition-height/transition-height'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'boost-api-call': BoostApiCall,
    'btn': Btn,
    'index': Index,
    'multi-store-options': MultiStoreOptions,
    'multi-store': MultiStore,
    'nosto': Nosto,
    'overlay': Overlay,
    'pre-header-banner': PreHeaderBanner,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'responsive-video': ResponsiveVideo,
    'site-header': SiteHeader,
    'transition-height': TransitionHeight,
    // canvas-global-object-end
  }
}

/**
 * Import Vuex stores here.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import MainCollectionStore from '~stores/main-collection'
import MenuDrawerStore from '~stores/menu-drawer'
import MultiStoreStore from '~stores/multi-store'
import OverlaysStore from '~stores/overlays'
import NostoStore from '~stores/nosto'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'main-collection': MainCollectionStore,
    'menu-drawer': MenuDrawerStore,
    'multi-store': MultiStoreStore,
    'overlays': OverlaysStore,
    'nosto': NostoStore,
    // canvas-stores-object-end
  }
}
